import { useCallback, useEffect, useState } from 'react';

/**
 * Physical device tilt from the accelerometer (`deviceorientation`).
 *
 * When the display orientation is locked to portrait (the force-vertical
 * case), `screen.orientation.angle` can no longer tell us which way the user
 * physically rotated the phone. `gamma` (left-right tilt, [-90, 90]) is the one
 * runtime signal whose sign flips between the two landscape hold directions, so
 * it is the candidate input for choosing +90 vs -90 rotation. This hook only
 * powers the /rotation-test validation page.
 */
export interface DeviceTilt {
  supported: boolean;
  permission: 'unknown' | 'granted' | 'denied' | 'not-required';
  alpha: number | null;
  beta: number | null;
  gamma: number | null;
  eventCount: number;
}

interface DeviceOrientationEventStatic {
  requestPermission?: () => Promise<'granted' | 'denied'>;
}

const getCtor = (): DeviceOrientationEventStatic | undefined =>
  (window as unknown as { DeviceOrientationEvent?: DeviceOrientationEventStatic })
    .DeviceOrientationEvent;

export function useDeviceTilt(): DeviceTilt & { requestPermission: () => Promise<void> } {
  const [tilt, setTilt] = useState<DeviceTilt>(() => ({
    supported: typeof window !== 'undefined' && 'DeviceOrientationEvent' in window,
    permission: 'unknown',
    alpha: null,
    beta: null,
    gamma: null,
    eventCount: 0,
  }));

  const requestPermission = useCallback(async () => {
    const ctor = getCtor();
    if (!ctor?.requestPermission) {
      setTilt((prev) => ({ ...prev, permission: 'not-required' }));
      return;
    }
    try {
      const result = await ctor.requestPermission();
      setTilt((prev) => ({
        ...prev,
        permission: result === 'granted' ? 'granted' : 'denied',
      }));
    } catch {
      setTilt((prev) => ({ ...prev, permission: 'denied' }));
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handler = (event: DeviceOrientationEvent) => {
      setTilt((prev) => ({
        ...prev,
        alpha: event.alpha,
        beta: event.beta,
        gamma: event.gamma,
        eventCount: prev.eventCount + 1,
      }));
    };

    window.addEventListener('deviceorientation', handler);

    // Android Chrome delivers events without a permission prompt; iOS needs an
    // explicit requestPermission() from a user gesture (the Enable button).
    if (!getCtor()?.requestPermission) {
      setTilt((prev) => ({ ...prev, permission: 'not-required' }));
    }

    return () => window.removeEventListener('deviceorientation', handler);
  }, []);

  return { ...tilt, requestPermission };
}
